import ScienceOutlined from '@mui/icons-material/ScienceOutlined';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import InputBase from '@mui/material/InputBase';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useBusinessUnits } from 'contexts/BusinessUnitsContext';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { type BusinessUnitPromptResult, useBusinessUnitPromptQuery } from 'services/api/playground.api';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

interface BusinessUnitGateProps {
  onLoad: (businessUnitId: number, result: BusinessUnitPromptResult) => void;
}

/**
 * Blank-start gate: pick a business unit, then load its interpolated system prompt + tools + model.
 * The sandbox stays hidden until a prompt is loaded.
 */
const BusinessUnitGate: FC<BusinessUnitGateProps> = ({ onLoad }) => {
  const { businessUnits, companyNames, getBusinessUnitsByCompany, isLoading: listLoading } = useBusinessUnits();
  const [selectedId, setSelectedId] = useState<number | ''>('');
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const filterRef = useRef<HTMLInputElement>(null);

  const { data, isFetching, isError } = useBusinessUnitPromptQuery(
    typeof selectedId === 'number' ? { businessUnitId: selectedId } : { businessUnitId: 0 },
    { skip: typeof selectedId !== 'number' }
  );

  useEffect(() => {
    if (typeof selectedId === 'number' && data) {
      onLoad(selectedId, data);
    }
  }, [data, selectedId, onLoad]);

  const normalized = filter.trim().toLowerCase();
  const groupedCompanies = useMemo(
    () =>
      companyNames
        .map((company) => ({
          company,
          units: getBusinessUnitsByCompany(company).filter(
            (bu) => bu.name.toLowerCase().includes(normalized) || company.toLowerCase().includes(normalized)
          ),
        }))
        .filter((group) => group.units.length > 0),
    [companyNames, getBusinessUnitsByCompany, normalized]
  );

  return (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Box
        sx={{
          width: 'min(480px, 100%)',
          bgcolor: SURFACE_CONTAINER_LOWEST,
          borderRadius: RADIUS_CARD,
          boxShadow: SHADOW,
          p: 4,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: alpha(YELLOW, 0.25),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <ScienceOutlined sx={{ fontSize: 28, color: NAVY }} />
        </Box>
        <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: 20, color: NAVY }}>
          Sandbox de conversación
        </Typography>
        <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: 13.5, color: alpha(NAVY, 0.6), mt: 0.5, mb: 3 }}>
          Elige una unidad de negocio para cargar su prompt y empezar a simular.
        </Typography>

        <Select
          value={selectedId}
          displayEmpty
          fullWidth
          open={open}
          onOpen={() => {
            setOpen(true);
            setTimeout(() => filterRef.current?.focus(), 0);
          }}
          onClose={() => {
            setOpen(false);
            setFilter('');
          }}
          onChange={(e) => setSelectedId(e.target.value as number)}
          disabled={listLoading}
          MenuProps={{
            sx: {
              '& .MuiPaper-root': {
                mt: 0.5,
                maxHeight: 380,
                borderRadius: RADIUS_CARD,
                background: GLASS_BACKGROUND,
                backdropFilter: GLASS_BLUR,
                boxShadow: SHADOW,
              },
            },
          }}
          renderValue={() =>
            typeof selectedId !== 'number' ? (
              <Typography sx={{ color: alpha(NAVY, 0.5), fontFamily: 'Jost, sans-serif', fontSize: 14 }}>
                Unidad de negocio…
              </Typography>
            ) : (
              businessUnits?.find((bu) => bu.id === selectedId)?.name || 'Unidad de negocio'
            )
          }
          sx={{
            textAlign: 'left',
            borderRadius: RADIUS_ITEM,
            bgcolor: SURFACE_CONTAINER_LOW,
            fontFamily: 'Jost, sans-serif',
            fontSize: 14,
            color: NAVY,
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          }}
        >
          <ListSubheader
            sx={{
              px: 1.5,
              pt: 1,
              pb: 1,
              position: 'sticky',
              top: 0,
              zIndex: 2,
              background: 'rgba(249, 250, 253, 0.97)',
              backdropFilter: 'blur(8px)',
              boxShadow: `0 1px 0 ${alpha(NAVY, 0.06)}`,
              lineHeight: 'unset',
            }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <InputBase
              inputRef={filterRef}
              placeholder="Buscar empresa o unidad..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              fullWidth
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: alpha(NAVY, 0.4) }} />
                </InputAdornment>
              }
              sx={{
                fontSize: 13,
                color: NAVY,
                bgcolor: alpha(NAVY, 0.04),
                borderRadius: '10px',
                px: 1,
                py: 0.5,
                '& input': { py: 0 },
              }}
            />
          </ListSubheader>
          {groupedCompanies.flatMap((group) => [
            <ListSubheader
              key={`company-${group.company}`}
              sx={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: alpha(NAVY, 0.5),
                background: 'transparent',
                lineHeight: '30px',
              }}
            >
              {group.company}
            </ListSubheader>,
            ...group.units.map((bu) => (
              <MenuItem key={bu.id} value={bu.id} sx={{ pl: 3, fontFamily: 'Jost, sans-serif', fontSize: 13.5 }}>
                {bu.name}
              </MenuItem>
            )),
          ])}
        </Select>

        {isFetching && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={16} sx={{ color: NAVY }} />
            <Typography sx={{ fontSize: 12.5, color: alpha(NAVY, 0.6), fontFamily: 'Jost, sans-serif' }}>
              Cargando prompt…
            </Typography>
          </Box>
        )}
        {isError && (
          <Typography sx={{ mt: 2, fontSize: 12.5, color: '#E7233B', fontFamily: 'Jost, sans-serif' }}>
            No se pudo cargar el prompt de esta unidad. Intenta con otra.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default BusinessUnitGate;
